import { action, makeObservable, observable } from 'mobx';
import { socialAuthError, socialAuthSuccess } from '@wix/bi-logger-hls2/v2';
import webBiLogger from '@wix/web-bi-logger';
import {
  StateMachineResponse,
  StateType,
  FactorType,
} from '@wix/ambassador-iam-authentication-v1-authentication/types';
import { RootStore } from './root';
import { openIdpPopup } from '../utils/openIdpPopup';
import {
  ERROR_CODES,
  extractServerErrorCode,
  extractRedirectUrl,
} from '../utils/errorHandler';
import { ROUTES } from '../routes';
import { AUTH_TYPE } from '../components/BlockedAccount/authTypes';

// Custom state for requiring first factor authentication (e.g., password)
// This state is returned by the IDP popup when password verification is needed
const REQUIRE_FIRST_FACTOR_STATE = 'REQUIRE_FIRST_FACTOR';

function extractEmailFromIdpResponse(
  response: StateMachineResponse,
): string | undefined {
  // IAM v1 standard identity email
  if (response.identity?.email?.address) {
    return response.identity.email.address;
  }
  // Legacy identity profile format (multiple emails array)
  if (response.identity?.identityProfile?.emails?.[0]) {
    return response.identity.identityProfile.emails[0];
  }
  // CommonCustomValue in additionalData (some IDP providers return email here)
  if (response.additionalData?.email?.strValue) {
    return response.additionalData.email.strValue;
  }
  return undefined;
}

// The IDP popup may return state as a structured object rather than a plain StateType enum value
interface IamStateObject {
  status?: string;
  stateToken?: string;
}

/**
 * Store for handling IDP (Identity Provider) connection login flow.
 * Similar to social login but uses the IDP popup flow for authentication.
 */
export class IdpConnectionLoginStore {
  protected readonly rootStore: RootStore;
  isLoading: boolean = false;
  hasError: boolean = false;
  errorCode: string = '';

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeObservable(this, {
      isLoading: observable,
      hasError: observable,
      errorCode: observable,
      loginWithIdpConnection: action.bound,
      handleError: action.bound,
      clearErrors: action.bound,
    });
  }

  /**
   * Initiates login via an Identity Provider connection.
   *
   * @param idpConnectionId - The unique identifier of the IDP connection
   * @param ldSessionId - The login dialog session ID for tracking
   * @returns Promise resolving to login result with success status
   */
  async loginWithIdpConnection(
    idpConnectionId: string,
    ldSessionId: string,
  ): Promise<{ success: boolean; errorCode?: string }> {
    this.isLoading = true;
    this.hasError = false;
    this.errorCode = '';

    try {
      const loginResponse = await openIdpPopup({
        idpConnectionId,
        ldSessionId,
        extraCustomPayload:
          this.rootStore.apiStore.buildIdpCustomPayloadExtras(),
      });

      return await this.handleLoginSuccess(loginResponse, idpConnectionId);
    } catch (error: any) {
      return this.handleLoginError(error, idpConnectionId);
    } finally {
      this.isLoading = false;
    }
  }

  private async handleLoginSuccess(
    loginResponse: StateMachineResponse,
    idpConnectionId: string,
  ): Promise<{ success: boolean }> {
    // Check if first factor (password) is required
    if (this.isPasswordRequired(loginResponse)) {
      return this.handlePasswordRequired(loginResponse, idpConnectionId);
    }

    // Check for MFA challenge
    const stateObj = loginResponse.state as unknown as IamStateObject;
    const stateValue =
      stateObj?.status ?? (loginResponse.state as unknown as string);
    if (stateValue === StateType.MFA_CHALLENGE && loginResponse.stateToken) {
      this.rootStore.twoFactorAuthStore.setAuthParamsMfaIAM(loginResponse);
      this.rootStore.navigationStore.navigate(ROUTES.TWO_FACTOR_AUTH);
      return { success: false };
    }

    // Only proceed to success if state is SUCCESS or DONE
    const isSuccessState =
      stateValue === StateType.SUCCESS ||
      stateValue === 'SUCCESS' ||
      stateValue === 'DONE';
    if (!isSuccessState) {
      this.handleError(`Unexpected state: ${stateValue}`);
      return { success: false };
    }

    this.rootStore.biLogger = webBiLogger.factory().logger();
    this.rootStore.postLoginStore.biLogger = webBiLogger.factory().logger();

    this.rootStore.biLogger.report(
      socialAuthSuccess({
        socialNetwork: idpConnectionId,
        context: this.rootStore.emailStepStore.getContext(),
      }),
    );

    // Navigate to post-login flow
    // additionalData may contain uaToken, authMethod, etc.
    const additionalData = loginResponse.additionalData ?? {};
    this.rootStore.navigationStore.postLogin({
      uaToken: additionalData.uaToken?.strValue,
      authMethod: additionalData.authMethod?.strValue,
    });

    return { success: true };
  }

  /**
   * Check if password is required as first factor.
   * This can happen when:
   * - state.status is REQUIRE_FIRST_FACTOR (from IDP popup response)
   * - State is MFA_CHALLENGE with PASSWORD factor type
   * - State is REQUIRE_MFA with PASSWORD in available factors
   */
  private isPasswordRequired(response: StateMachineResponse): boolean {
    const stateObj = response.state as unknown as IamStateObject;
    const stateValue =
      stateObj?.status ?? (response.state as unknown as string);
    if (stateValue === REQUIRE_FIRST_FACTOR_STATE) {
      return true;
    }

    // Check for MFA_CHALLENGE with PASSWORD factor
    if (
      response.state === StateType.MFA_CHALLENGE &&
      response.mfaChallengeData?.factorType === FactorType.PASSWORD
    ) {
      return true;
    }

    // Check for REQUIRE_MFA with PASSWORD in available factors
    if (
      response.state === StateType.REQUIRE_MFA &&
      response.requireMfaData?.availableFactors?.some(
        (factor) => factor.factorType === FactorType.PASSWORD,
      )
    ) {
      return true;
    }

    return false;
  }

  /**
   * Handle case where password verification is required.
   * Navigate to the RequireFirstFactor page.
   */
  private handlePasswordRequired(
    response: StateMachineResponse,
    idpConnectionId: string,
  ): { success: boolean } {
    const email = extractEmailFromIdpResponse(response);

    if (email) {
      this.rootStore.userDataStore.setEmail(email);
    }

    // Store the state token for password verification
    // Token can be in state.stateToken (IDP popup format) or response.stateToken
    const stateObj = response.state as unknown as IamStateObject;
    const stateToken = stateObj?.stateToken ?? response.stateToken;
    if (stateToken) {
      this.rootStore.requireFirstFactorStore.setStateToken(stateToken);
    }

    this.rootStore.setActiveSocialProvider(idpConnectionId);
    this.rootStore.navigationStore.navigate(ROUTES.REQUIRE_FIRST_FACTOR);

    return { success: false };
  }

  private handleLoginError(
    error: any,
    idpConnectionId: string,
  ): { success: boolean; errorCode?: string } {
    const errorCode = extractServerErrorCode(error);

    this.rootStore.biLogger.report(
      socialAuthError({
        socialNetwork: idpConnectionId,
        reason: errorCode || error?.message || 'Unknown error',
      }),
    );

    // Handle specific error cases
    if (errorCode === ERROR_CODES.USER_IS_BLOCKED) {
      this.rootStore.navigationStore.navigateToBlockedAccount({
        refferal_info: AUTH_TYPE.SOCIAL,
      });
      return { success: false, errorCode };
    }

    if (errorCode === ERROR_CODES.SSO_LOGIN_MANDATORY_ERROR) {
      // User has SSO connection - redirect to SSO login
      const redirectUrl = extractRedirectUrl(error);
      if (redirectUrl) {
        this.rootStore.navigationStore.redirect(redirectUrl);
        return { success: false, errorCode };
      }
    }

    if (errorCode === ERROR_CODES.SOCIAL_LOGIN_GET_EMAIL) {
      // User needs to provide email - navigate to social enter email page
      this.rootStore.setActiveSocialProvider(idpConnectionId);
      this.rootStore.navigationStore.navigate(ROUTES.SOCIAL_ENTER_EMAIL);
      return { success: false, errorCode };
    }

    if (errorCode === ERROR_CODES.SOCIAL_LOGIN_VERIFY_ACCOUNT) {
      // Account verification needed
      if (error.errorDescription) {
        this.rootStore.userDataStore.setEmail(error.errorDescription);
      }
      this.rootStore.setActiveSocialProvider(idpConnectionId);
      this.rootStore.navigationStore.navigate(ROUTES.ACCOUNT_VERIFICATION);
      return { success: false, errorCode };
    }

    // Generic error handling
    this.hasError = true;
    this.errorCode = errorCode || 'UNKNOWN_ERROR';

    return { success: false, errorCode: this.errorCode };
  }

  handleError(error?: string) {
    this.hasError = true;
    this.errorCode = error || 'UNKNOWN_ERROR';
  }

  clearErrors() {
    this.hasError = false;
    this.errorCode = '';
  }
}
